import { useEffect, useRef, useState } from 'react';
import { useMessages } from '../contexts/MessagesContext';
import { useContacts } from '../contexts/ContactsContext';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Paperclip, Send, Search, Smile, ThumbsUp } from 'lucide-react';
import AttachmentModal from '../components/modals/AttachmentModal';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

type FilterType = 'all' | 'unread' | 'read';

interface SelectedAttachment {
  fileUrl: string;
  fileName: string;
}

export default function Messages() {
  const { messages, sendMessage, markMessagesAsRead } = useMessages();
  const { contacts } = useContacts();
  const { currentUser } = useAuth();
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [showContactsList, setShowContactsList] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<SelectedAttachment | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<SelectedAttachment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  // Ordenar contatos por mensagens não lidas e última mensagem
  const sortedContacts = [...contacts]
    .reduce((acc, current) => {
      const existingContact = acc.find(
        contact => (contact.contactId === current.contactId || contact.userId === current.contactId)
      );
      if (!existingContact) {
        acc.push(current);
      } else if (current.lastMessage && (!existingContact.lastMessage || 
        new Date(current.lastMessage.timestamp).getTime() > new Date(existingContact.lastMessage.timestamp).getTime())) {
        const index = acc.indexOf(existingContact);
        acc[index] = current;
      }
      return acc;
    }, [] as typeof contacts)
    .sort((a, b) => {
      // Primeiro ordenar por mensagens não lidas
      if (a.unreadCount !== b.unreadCount) {
        return b.unreadCount - a.unreadCount;
      }
      // Depois por última mensagem
      if (a.lastMessage && b.lastMessage) {
        return new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime();
      }
      if (a.lastMessage) return -1;
      if (b.lastMessage) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

  // Filtrar contatos
  const filteredContacts = sortedContacts
    .filter(contact => {
      if (filter === 'unread') return contact.unreadCount > 0;
      if (filter === 'read') return contact.unreadCount === 0;
      return true;
    })
    .filter(contact => 
      contact.contactName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessageInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleSendLike = async () => {
    if (!selectedContact) return;
    try {
      await sendMessage(selectedContact, '👍', null);
    } catch (error) {
      console.error('Erro ao enviar like:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedContact) {
      markMessagesAsRead(selectedContact);
    }
  }, [selectedContact, messages]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Preview the file
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setSelectedAttachment({
        fileUrl: base64String,
        fileName: file.name
      });
    };
    reader.readAsDataURL(file);

    // Store the actual file for upload
    setSelectedFile(file);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact || (!messageInput.trim() && !selectedFile)) return;

    try {
      await sendMessage(
        selectedContact,
        messageInput.trim(),
        selectedFile
      );
      setMessageInput('');
      setSelectedAttachment(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    }
  };

  return (
    <div className="flex h-[calc(100vh-160px)] overflow-hidden bg-gray-50">
      {/* Lista de contatos - Coluna da esquerda (responsiva) */}
      <div className={`
        md:w-[280px] bg-white border-r border-gray-200 flex flex-col
        fixed md:relative
        w-full h-full md:h-auto
        ${showContactsList ? 'left-0' : '-left-full md:left-0'}
        transition-all duration-300 ease-in-out
        z-20
      `}>
        <div className="flex-shrink-0 p-3 border-b border-gray-200 flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Mensagens</h2>
            <button 
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setShowContactsList(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Filtros */}
          <div className="flex gap-1 text-sm">
            <button
              onClick={() => setFilter('all')}
              className={`px-2 py-1 rounded-full transition-colors ${
                filter === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-2 py-1 rounded-full transition-colors ${
                filter === 'unread'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Não lidas
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-2 py-1 rounded-full transition-colors ${
                filter === 'read'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Lidas
            </button>
          </div>

          {/* Barra de pesquisa */}
          <div className="relative">
            <input
              type="text"
              placeholder="Procurar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {!currentUser ? (
            <div className="text-center text-gray-500 mt-8 p-4">
              <p className="text-lg">Inicie sessão para ver os seus contactos.</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center text-gray-500 mt-8 p-4">
              <p className="text-lg">Nenhum contacto encontrado.</p>
              <p className="text-sm mt-2">Adicione contactos através dos perfis de utilizador.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`flex items-center p-3 hover:bg-gray-50 transition-colors cursor-pointer
                    ${selectedContact === contact.contactId ? 'bg-blue-50 border-l-4 border-blue-500' : ''}
                  `}
                  onClick={() => setSelectedContact(contact.contactId)}
                >
                  <div className="relative flex-shrink-0">
                    {contact.contactPhoto ? (
                      <img
                        src={contact.contactPhoto}
                        alt={contact.contactName}
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-gray-200">
                        <span className="text-lg text-blue-500 font-semibold">
                          {contact.contactName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div 
                      className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border-2 border-white ${
                        contact.isOnline ? 'bg-green-500' : 'bg-gray-400'
                      }`} 
                      title={contact.isOnline ? 'Online' : 'Offline'}
                    />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-gray-900 truncate">{contact.contactName}</h3>
                      {contact.lastMessage && (
                        <span className="text-xs text-gray-500">
                          {contact.lastMessage.timestamp && !isNaN(new Date(contact.lastMessage.timestamp).getTime()) 
                            ? format(new Date(contact.lastMessage.timestamp), 'HH:mm')
                            : ''}
                        </span>
                      )}
                    </div>
                    {contact.lastMessage && (
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {contact.lastMessage.content}
                      </p>
                    )}
                  </div>
                  {contact.unreadCount > 0 && contact.lastMessage?.senderId !== currentUser?.uid && (
                    <div className="ml-2 flex-shrink-0">
                      <span className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded-full">
                        {contact.unreadCount}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Área de chat - Coluna da direita */}
      <div className="flex-1 flex flex-col h-full bg-white relative">
        {selectedContact ? (
          <>
            {/* Cabeçalho do chat */}
            <div className="flex-shrink-0 py-2 px-4 border-b border-gray-200 bg-white">
              <div className="flex items-center">
                <button 
                  className="md:hidden mr-3 text-gray-600 hover:text-gray-900"
                  onClick={() => setShowContactsList(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="flex flex-col">
                  <h3 className="font-medium text-gray-900">
                    {contacts.find((c) => c.contactId === selectedContact)?.contactName}
                  </h3>
                  <div className="flex items-center mt-1">
                    <div 
                      className={`w-2 h-2 rounded-full mr-2 ${
                        contacts.find((c) => c.contactId === selectedContact)?.isOnline 
                          ? 'bg-green-500' 
                          : 'bg-gray-400'
                      }`}
                    />
                    <span className="text-xs text-gray-500">
                      {contacts.find((c) => c.contactId === selectedContact)?.isOnline 
                        ? 'Online' 
                        : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => window.location.href = `/dashboard/social/profile/${selectedContact}`}
                className="p-2 text-gray-600 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
                title="Ver perfil social"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            </div>

            {/* Área de mensagens */}
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2 p-3 pb-2">
                {messages
                  .filter(
                    (m) =>
                      (m.senderId === selectedContact && m.receiverId === currentUser?.uid) ||
                      (m.senderId === currentUser?.uid && m.receiverId === selectedContact)
                  )
                  .sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())
                  .map((message, index, array) => {
                    const isSender = message.senderId === currentUser?.uid;
                    const messageDate = message.timestamp.toDate();
                    const showDate =
                      index === 0 ||
                      messageDate.toDateString() !== array[index - 1].timestamp.toDate().toDateString();

                    return (
                      <div key={message.id}>
                        {showDate && (
                          <div className="text-center my-2">
                            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                              {format(message.timestamp.toDate(), "d 'de' MMMM", { locale: ptBR })}
                            </span>
                          </div>
                        )}
                        <div
                          className={`flex items-end space-x-2 ${
                            isSender ? 'justify-end' : 'justify-start'
                          } group`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-2 ${
                              isSender
                                ? 'bg-blue-500 text-white rounded-br-none'
                                : 'bg-white text-gray-900 rounded-bl-none shadow-sm'
                            }`}
                          >
                            {message.fileUrl && (
                              <button
                                onClick={() => setViewingAttachment({
                                  fileUrl: message.fileUrl!,
                                  fileName: message.fileName || 'Anexo'
                                })}
                                className={`block mb-1 text-sm ${
                                  isSender
                                    ? 'text-blue-100'
                                    : 'text-blue-500'
                                }`}
                              >
                                📎 {message.fileName || 'Anexo'}
                              </button>
                            )}
                            {message.content && (
                              <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                            )}
                            <div className="flex items-center justify-end mt-1 space-x-1">
                              <span
                                className={`text-xs ${
                                  isSender ? 'text-blue-100' : 'text-gray-500'
                                }`}
                              >
                                {format(message.timestamp.toDate(), 'HH:mm')}
                              </span>
                              {isSender && (
                                <span className="text-blue-100">
                                  {message.read ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Área de input da mensagem */}
            <div className="flex-shrink-0 py-2 px-4 border-t border-gray-200 bg-white">
              <form onSubmit={handleSendMessage} className="flex items-end gap-1">
                <div className="relative flex-1">
                  {/* Botão de emoji */}
                  <button
                    type="button"
                    ref={emojiButtonRef}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="absolute left-2 bottom-2 text-gray-500 hover:text-gray-700"
                    title="Escolher emoji"
                  >
                    <Smile size={16} />
                  </button>

                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-2 z-50">
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        lazyLoadEmojis={true}
                      />
                    </div>
                  )}

                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Escreva uma mensagem..."
                    className="w-full pl-8 pr-3 py-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none text-sm h-8"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                </div>

                {/* Botões de ação */}
                <div className="flex-shrink-0 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 text-gray-500 hover:text-gray-700"
                    title="Anexar arquivo"
                  >
                    <Paperclip size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={handleSendLike}
                    className="p-1.5 text-blue-500 hover:text-blue-700"
                    title="Enviar like"
                  >
                    <ThumbsUp size={16} />
                  </button>

                  <button
                    type="submit"
                    className="p-1.5 text-blue-500 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!messageInput.trim() && !selectedFile}
                    title="Enviar mensagem"
                  >
                    <Send size={16} />
                  </button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Selecione um contacto para iniciar uma conversa
          </div>
        )}
      </div>

      {viewingAttachment && (
        <AttachmentModal
          fileUrl={viewingAttachment.fileUrl}
          fileName={viewingAttachment.fileName}
          onClose={() => setViewingAttachment(null)}
        />
      )}
    </div>
  );
}
